import { useMutation, useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useRouter } from 'next/router'
import { ILesson, ILessonPrestudyListItem } from '../../../model/lesson.interface'
import apis from '../../apis'
import { axiosPost } from '../../axiosHelper'
import {
  cafJobGraphDataQueryApi,
  cafJobIdQueryApi,
  cafJobLevelDataQueryApi,
  cafJobStatusQueryApi,
  cafLessonDiffFeedbackQueryApiV5,
  cafLessonDiffQueryApi,
  cafLessonDiffQueryApiV5,
  cafLessonStatusQueryApi,
  cafLessonStatusQueryApiV5,
  feedbackStatusQueryApiV5,
  jobOptionsQueryApi,
  lessonDetailApi,
  lessonPreparationQueryApi,
  lessonPreparationQueryApiV5,
  lessonReviewApi,
  lessonReviewMonyhlyCountApi,
  lessonSettingDetailApi,
  scriptStatusQueryApiV5,
} from '../../react-query/queryApis'

export enum PAST_FILTER_TYPE {
  ENTIRE = 'entire',
  COMPLETE = 'complete',
  INCOMPLETE = 'incomplete',
  NOSHOW = 'noshow',
}

export interface ILessonPreparation {
  success: boolean
  lesson: ILesson
  prestudy_list: ILessonPrestudyListItem[]
}

export const useLessonPreparation = () => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }

  return useQuery<ILessonPreparation, AxiosError>({
    queryKey: lessonPreparationQueryApi.getQueryKey({ pathParams }),
    queryFn: lessonPreparationQueryApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
  })
}

export const useLessonPreparationV5 = () => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }

  return useQuery<ILessonPreparation, AxiosError>({
    queryKey: lessonPreparationQueryApiV5.getQueryKey({ pathParams }),
    queryFn: lessonPreparationQueryApiV5.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
  })
}

export const useCafLessonStatus = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }

  return useQuery<any, AxiosError>({
    queryKey: cafLessonStatusQueryApi.getQueryKey({ pathParams }),
    queryFn: cafLessonStatusQueryApi.getQueryFn({ pathParams }),

    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}
export const useCafLessonStatusV5 = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }

  return useQuery<any, AxiosError>({
    queryKey: cafLessonStatusQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafLessonStatusQueryApiV5.getQueryFn({ pathParams }),

    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}

export interface ILessonDiff {
  band_diff: number
  is_band_changed: boolean
  caf_analysis_component_id: number
  id: number
  tag: number
  reasoning_data: {
    main: number[]
    sub: number[]
    type: 'C' | 'A' | 'F'
  }
}
export const useCafLessonDiff = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }
  return useQuery<ILessonDiff[], AxiosError>({
    queryKey: cafLessonDiffQueryApi.getQueryKey({ pathParams }),
    queryFn: cafLessonDiffQueryApi.getQueryFn({ pathParams }),

    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}
export const useCafLessonDiffV5 = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }
  return useQuery<ILessonDiff[], AxiosError>({
    queryKey: cafLessonDiffQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafLessonDiffQueryApiV5.getQueryFn({ pathParams }),

    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}

export const useCafLessonDiffFeedbackV5 = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }
  return useQuery<any, AxiosError>({
    queryKey: cafLessonDiffFeedbackQueryApiV5.getQueryKey({ pathParams }),
    queryFn: cafLessonDiffFeedbackQueryApiV5.getQueryFn({ pathParams }),

    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}

export interface IGraphData {
  min: number
  max: number
  average: number
  top: number
  bottom: number
}

export interface GraphData {
  complexity: IGraphData
  fluency: IGraphData
  accuracy: IGraphData
  pronunciation: IGraphData
}

const initialGraphData: GraphData = {
  complexity: {
    min: 0,
    max: 0,
    average: 0,
    top: 0,
    bottom: 0,
  },
  fluency: {
    min: 0,
    max: 0,
    average: 0,
    top: 0,
    bottom: 0,
  },
  accuracy: {
    min: 0,
    max: 0,
    average: 0,
    top: 0,
    bottom: 0,
  },
  pronunciation: {
    min: 0,
    max: 0,
    average: 0,
    top: 0,
    bottom: 0,
  },
}

export const useCafJobGraphData = (jobId: number) => {
  const pathParams = { jobId }
  return useQuery<GraphData, AxiosError>({
    queryKey: cafJobGraphDataQueryApi.getQueryKey({ pathParams }),
    queryFn: cafJobGraphDataQueryApi.getQueryFn({ pathParams }),

    initialData: initialGraphData,

    // onError: (e) => handleAxiosError(e),
    retry: 5,
    enabled: !!jobId,
  })
}

export interface IJobId {
  job_id: number
}

export const useCafJobId = () => {
  return useQuery<IJobId, AxiosError>({
    queryKey: cafJobIdQueryApi.getQueryKey(),
    queryFn: cafJobIdQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: 5,
  })
}

export interface LevelData {
  levels: {
    complexity: string
    accuracy: string
    fluency: string
    pronunciation: string
  }
  percents: {
    complexity: number
    accuracy: number
    fluency: number
    pronunciation: number
  }
}

const initialLevelData = {
  levels: {
    complexity: '0',
    accuracy: '0',
    fluency: '0',
    pronunciation: '0',
  },
  percents: {
    complexity: 100,
    accuracy: 100,
    fluency: 100,
    pronunciation: 100,
  },
}

export const useCafJobLevelData = (jobId: number) => {
  const pathParams = { jobId }
  return useQuery<LevelData, AxiosError>({
    queryKey: cafJobLevelDataQueryApi.getQueryKey({ pathParams }),
    queryFn: cafJobLevelDataQueryApi.getQueryFn({ pathParams }),
    initialData: initialLevelData,
    // onError: (e) => handleAxiosError(e),
    retry: 5,
    enabled: !!jobId,
  })
}

export enum UserJobStatus {
  SHOW = 'show',
  REQUIRE_INFO = 'require_info',
  REQUIRE_LESSON = 'require_lesson',
  REQUIRE_TRIAL = 'require_trial',
  REQUIRE_PURCHASE = 'require_purchase',
}

export const useCafJobStatus = () => {
  return useQuery<UserJobStatus, AxiosError>({
    queryKey: cafJobStatusQueryApi.getQueryKey(),
    queryFn: cafJobStatusQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: 5,
  })
}

export interface IJobOptionDetail {
  id: number
  title: string
}

export interface IJobOptions {
  job: IJobOptionDetail[]
  job_year: IJobOptionDetail[]
  job_type: IJobOptionDetail[]
  foreign_experience: IJobOptionDetail[]
  foreign_experience_year: IJobOptionDetail[]
  category: string[]
}

export const useJobOptions = () => {
  return useQuery<IJobOptions, AxiosError>({
    queryKey: jobOptionsQueryApi.getQueryKey(),
    queryFn: jobOptionsQueryApi.getQueryFn(),

    // onError: (e) => handleAxiosError(e),
    retry: 5,
  })
}

interface ISubmitJobs {
  success: boolean
}
interface ISubmitJobsPayload {
  category: string
  selection?: number
  multiselections?: number[]
  other?: string
}

export const useSubmitJobs = () => {
  return useMutation<ISubmitJobs, AxiosError, ISubmitJobsPayload>({
    mutationFn: (payload) => {
      return axiosPost(apis.caf.jobAnalysis.submit(), payload)
    },
    // onError: (error) => handleAxiosError(error),
  })
}

export interface ILessonReviewData {
  success: boolean
  // API 결정 후 past_lessons 작성 - 개발중
  past_lessons: any[]
  entire_lessons_count: number
  incompleted_lessons_count: number
  completed_lessons_count: number
  noshow_lessons_count: number
  pagination_count: number
  exist_dates: any[]
  latest_lesson_year: number
  latest_lesson_month: number
  latest_not_review_lesson_year: number
  latest_not_review_lesson_month: number
}

export const useLessonReviewData = (year, month, type = PAST_FILTER_TYPE.ENTIRE) => {
  const searchParams = { year: year, month: month, type: type }
  return useQuery<ILessonReviewData, AxiosError>({
    queryKey: lessonReviewApi.getQueryKey({ searchParams }),
    queryFn: lessonReviewApi.getQueryFn({ searchParams }),
    refetchOnWindowFocus: true,
  })
}
export const useLessonReviewMonyhlyCount = (year) => {
  const searchParams = { year }
  return useQuery<ILessonReviewData, AxiosError>({
    queryKey: lessonReviewMonyhlyCountApi.getQueryKey({ searchParams }),
    queryFn: lessonReviewMonyhlyCountApi.getQueryFn({ searchParams }),
  })
}

export const useLessonInfoDetail = () => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }
  console.log('pathParams', pathParams)
  return useQuery<ILessonPreparation, AxiosError>({
    queryKey: lessonDetailApi.getQueryKey({ pathParams }),
    queryFn: lessonDetailApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
  })
}
export const useLessonSettingDetail = (lessonId: number) => {
  const pathParams = { lessonId }
  return useQuery<any, AxiosError>({
    queryKey: lessonSettingDetailApi.getQueryKey({ pathParams }),
    queryFn: lessonSettingDetailApi.getQueryFn({ pathParams }),

    // onError: (e) => handleAxiosError(e),
    retry: false,
  })
}

export const useFeedbackStatusV5 = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }
  return useQuery<any, AxiosError>({
    queryKey: feedbackStatusQueryApiV5.getQueryKey({ pathParams }),
    queryFn: feedbackStatusQueryApiV5.getQueryFn({ pathParams }),
    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}

export const useScriptStatusV5 = (onError = () => null) => {
  const router = useRouter()
  const lessonId = router.query?.lessonId
    ? typeof router.query.lessonId === 'string'
      ? parseInt(router.query.lessonId, 10)
      : 0
    : undefined
  const pathParams = { lessonId }
  return useQuery<any, AxiosError>({
    queryKey: scriptStatusQueryApiV5.getQueryKey({ pathParams }),
    queryFn: scriptStatusQueryApiV5.getQueryFn({ pathParams }),
    // onError: onError,
    retry: 5,
    enabled: !!lessonId,
  })
}
